import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    allowedDomainsPersoo,
    PERSOO_IDS,
    PERSOO_SEARCH_QUERY_PARAMETER_NAME,
    PERSOO_SEARCH_QUERY_PARAMETER_VALUE,
    PERSOO_SEARCH_TIMEOUT_SECONDS,
} from 'modules/persoo/persooTypes';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

type PersooHookProps = {
    elementId?: (typeof PERSOO_IDS)[keyof typeof PERSOO_IDS];
};

export const usePersoo = ({ elementId }: PersooHookProps = {}) => {
    const { domainId } = useDomainConfig();
    const router = useRouter();

    const isPersooAllowed = allowedDomainsPersoo.includes(domainId);
    const isPersooTriggered =
        !!Object.getOwnPropertyDescriptor(router.query, PERSOO_SEARCH_QUERY_PARAMETER_NAME) &&
        router.query[PERSOO_SEARCH_QUERY_PARAMETER_NAME] === PERSOO_SEARCH_QUERY_PARAMETER_VALUE;

    const shouldWaitForPersoo = isPersooAllowed && isPersooTriggered && elementId !== undefined;
    const [isTescomaFallbackVisible, setIsTescomaFallbackVisible] = useState(false);

    useEffect(() => {
        const element: HTMLElement | null = elementId ? document.getElementById(elementId) : null;
        if (!shouldWaitForPersoo || !element) {
            setIsTescomaFallbackVisible(false);

            return undefined;
        }

        setIsTescomaFallbackVisible(false);
        let hasTimedOut = false;
        const updateFallbackVisibility = () => {
            const hasPersooResults = element.innerHTML.trim() !== '';

            if (hasPersooResults) {
                setIsTescomaFallbackVisible(false);
            } else if (hasTimedOut) {
                setIsTescomaFallbackVisible(true);
            }
        };
        const observer = new MutationObserver(updateFallbackVisibility);
        observer.observe(element, { childList: true, subtree: true });

        const timeout = setTimeout(() => {
            hasTimedOut = true;
            updateFallbackVisibility();
        }, PERSOO_SEARCH_TIMEOUT_SECONDS * 1000);

        return () => {
            clearTimeout(timeout);
            observer.disconnect();
        };
    }, [elementId, shouldWaitForPersoo]);

    return {
        isPersooAllowed,
        isPersooTriggered,
        shouldDisplayTescomaResult: !shouldWaitForPersoo || isTescomaFallbackVisible,
    };
};
